/**
 * Pillar Cards - entrance animation frontend.
 *
 * Build entry: src/js/modules/pillar-cards/frontend.js
 * Output:      assets/js/modules/pillar-cards-frontend.min.js
 *
 * Scroll-triggered entrance animations using the same protocol as the
 * UiCore Animate plugin (`entrance-animation.js`): cards carry
 * `data-ui-animation` (one or more class names) and `data-ui-duration`
 * (fast/normal/slow) plus a per-card `--ui-anim-delay` custom property, and
 * start with the `uicore-animate-hide` class (opacity 0, visibility hidden).
 * When a card intersects the viewport we add `animated` (+ speed class) and
 * the animation-name classes, then remove `uicore-animate-hide` exactly when
 * the CSS animation starts (animationstart) - so the card stays hidden during
 * its stagger delay, with a next-frame fallback for zero-delay cards.
 *
 * The keyframes + `.animated` rules ship inside the widget's own scoped
 * style, so this works standalone. Visitors with prefers-reduced-motion get
 * their cards revealed immediately (CSS + this guard).
 *
 * Loaded only when:
 *   1. the Pillar Cards feature is enabled (module manager),
 *   2. Elementor renders the widget on the page (get_script_depends).
 *
 * Self-contained IIFE - no imports, no build-time shared chunks.
 */
(function () {
	'use strict';

	/** Respect the OS-level reduced-motion setting. */
	function prefersReducedMotion() {
		return (
			typeof window.matchMedia === 'function' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches
		);
	}

	// One shared IntersectionObserver for every card on the page.
	var observer = null;

	function getObserver() {
		if (observer) {
			return observer;
		}
		observer = new IntersectionObserver(
			function (entries) {
				entries.forEach(function (entry) {
					if (!entry.isIntersecting) {
						return;
					}
					var el = entry.target;

					// 1. Add the animation-speed class + `animated`.
					var speed = el.getAttribute('data-ui-duration');
					if (speed === 'fast') {
						el.classList.add('animated', 'animated-fast');
					} else if (speed === 'slow') {
						el.classList.add('animated', 'animated-slow');
					} else {
						el.classList.add('animated');
					}

					// 2. Add the animation-name class(es).
					var animName = el.getAttribute('data-ui-animation');
					if (animName) {
						el.classList.add.apply(el.classList, animName.split(' '));
					}

					// 3. Remove the hide class ONLY when the animation really
					// starts (keeps the card invisible during its delay).
					var onStart = function () {
						el.classList.remove('uicore-animate-hide');
						el.removeEventListener('animationstart', onStart);
					};
					el.addEventListener('animationstart', onStart, { once: true });

					// 4. Safety fallback: no delay -> reveal next frame.
					requestAnimationFrame(function () {
						if (getComputedStyle(el).animationDelay === '0s') {
							el.classList.remove('uicore-animate-hide');
						}
					});

					// 5. Stop observing after the trigger.
					observer.unobserve(el);
				});
			},
			{
				rootMargin: '10px',
				threshold: 0,
			}
		);
		return observer;
	}

	/** Wire one widget root (its `.pillar-card` children). */
	function initRoot(root) {
		if (!root || !root.querySelector) {
			return;
		}

		// Reduced motion: reveal everything immediately, never animate.
		if (prefersReducedMotion()) {
			var hidden = root.querySelectorAll('.uicore-animate-hide');
			for (var i = 0; i < hidden.length; i++) {
				hidden[i].classList.remove('uicore-animate-hide');
			}
			return;
		}

		var cards = root.querySelectorAll('[data-ui-animation]');
		for (var j = 0; j < cards.length; j++) {
			getObserver().observe(cards[j]);
		}
	}

	/** Sweep the document for every widget root. */
	function initAll() {
		var roots = document.querySelectorAll('.mypro-pillar-cards');
		for (var i = 0; i < roots.length; i++) {
			initRoot(roots[i]);
		}
	}

	/**
	 * Register the Elementor `element_ready` hook. `elementorFrontend.hooks`
	 * only exists after `elementorFrontend.init()` runs - on the public page
	 * at DOM ready, and in the editor preview after the parent editor boots
	 * and calls `elementorFrontend.init()` on the iframe's instance.
	 * `init()` dispatches the native "elementor/frontend/init" event on
	 * `window` at that exact moment, so we register from there rather than at
	 * script eval (where hooks does not exist yet inside the editor).
	 *
	 * `runReadyTrigger` fires `frontend/element_ready/<data-widget_type>` and
	 * the rendered wrapper always carries the skin suffix (e.g.
	 * `mypro_pillar_cards.default`), so the suffixed name is the one that
	 * actually fires; the bare name is registered too for older versions.
	 */
	function registerElementReadyHook() {
		if (
			!window.elementorFrontend ||
			!window.elementorFrontend.hooks ||
			window.elementorFrontend.hooks.__myproPillarCardsReady
		) {
			return;
		}
		window.elementorFrontend.hooks.__myproPillarCardsReady = true;

		var onReady = function ($scope) {
			initRoot($scope && $scope[0] ? $scope[0] : $scope);
		};
		window.elementorFrontend.hooks.addAction('frontend/element_ready/mypro_pillar_cards.default', onReady);
		window.elementorFrontend.hooks.addAction('frontend/element_ready/mypro_pillar_cards', onReady);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initAll);
	} else {
		initAll();
	}

	if (window.elementorFrontend && window.elementorFrontend.hooks) {
		registerElementReadyHook();
	}
	window.addEventListener('elementor/frontend/init', registerElementReadyHook);

	// Version-proof fallback: whenever a widget node appears after this script
	// ran (editor drag-in, control re-render, lazy tabs/accordions, late
	// renderers), a rAF-throttled sweep re-initializes it.
	var sweepQueued = false;
	function queueSweep() {
		if (sweepQueued) {
			return;
		}
		sweepQueued = true;
		requestAnimationFrame(function () {
			sweepQueued = false;
			initAll();
		});
	}
	if (typeof MutationObserver !== 'undefined') {
		new MutationObserver(function () {
			queueSweep();
		}).observe(document.documentElement, {
			childList: true,
			subtree: true,
		});
	}
})();
